import { useState } from "react"
import { Link } from "react-router-dom"
import {
  IconBell,
  IconCheck,
  IconChecks,
  IconCalendarEvent,
  IconMail,
  IconDoor,
} from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
} from "@/hooks/use-notifications"
import { useNotificationStore } from "@/stores/notification-store"
import { formatRelative } from "@/lib/dates"
import type { Notification } from "@/types/models"

function getNotifIcon(type: string) {
  if (type.includes("reservation")) return <IconCalendarEvent className="size-4" />
  if (type.includes("invitation")) return <IconMail className="size-4" />
  if (type.includes("room")) return <IconDoor className="size-4" />
  return <IconBell className="size-4" />
}

function NotificationItem({ notification, onRead }: { notification: Notification; onRead: (id: string) => void }) {
  const isUnread = !notification.read_at

  return (
    <div
      className={`group flex items-start gap-3 px-4 py-2.5 transition-colors ${
        isUnread ? "bg-primary/5" : "hover:bg-muted/50"
      }`}
    >
      <div className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md ${
        isUnread ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
      }`}>
        {getNotifIcon(notification.type)}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm leading-snug ${isUnread ? "font-medium text-foreground" : "text-muted-foreground"}`}>
          {notification.title}
        </p>
        {notification.body && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{notification.body}</p>
        )}
        <p className="mt-0.5 text-xs text-muted-foreground/60">
          {formatRelative(notification.created_at)}
        </p>
      </div>
      {isUnread && (
        <Button
          variant="ghost"
          size="icon"
          className="mt-0.5 size-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
          onClick={() => onRead(notification.id)}
        >
          <IconCheck className="size-3.5" />
        </Button>
      )}
    </div>
  )
}

export function NotificationPanel() {
  const { data, isLoading } = useNotifications()
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const unreadCount = useNotificationStore((state) => state.unreadCount)

  const notifications = data?.data ?? []
  const recent = notifications.slice(0, 10)

  const handleRead = async (id: string) => {
    await markAsRead.mutateAsync(id)
  }

  const handleReadAll = async () => {
    await markAllAsRead.mutateAsync()
  }

  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-8">
          <IconBell className="size-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full p-0 text-[10px]"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0">
        <div className="relative flex items-center justify-center rounded-t-lg border-b bg-sidebar px-4 py-2.5">
          <h4 className="text-sm font-semibold text-sidebar-foreground">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-2 h-auto px-2 py-1 text-xs text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={handleReadAll}
              disabled={markAllAsRead.isPending}
            >
              <IconChecks className="size-3.5" />
              Tout lire
            </Button>
          )}
        </div>
        <div className="max-h-[340px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Chargement…</p>
            </div>
          ) : recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <IconBell className="size-8 text-muted-foreground/30" />
              <p className="text-sm text-muted-foreground">Aucune notification</p>
            </div>
          ) : (
            recent.map((notif) => (
              <NotificationItem
                key={notif.id}
                notification={notif}
                onRead={handleRead}
              />
            ))
          )}
        </div>
        {recent.length > 0 && (
          <>
            <div className="flex justify-center rounded-b-lg border-t bg-sidebar px-4 py-2">
              <Button variant="ghost" size="sm" className="h-auto py-1 text-xs text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground" asChild>
                <Link to="/profile/notifications" onClick={() => setOpen(false)}>Préférences de notification</Link>
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
